/**
 * Constants for player-detail feature
 *
 * Contains static data, mock data, and configuration values.
 * Includes `getMetricLabelsWithCustom` for dynamically appending
 * organization-specific custom metrics to the standard metric label set.
 */

import type {
  ScoutingReport,
  PlayerNote,
  AIScoutingSummaryData,
  CompareCategoryOption,
  CompareItem,
} from "./types";

import { PlayerDetailSeasonCategoricalScoresFragment } from "@/generated/graphql";
import type { BackendCustomStats } from "@/types/customMetrics";
import { processDynamicCustomMetrics } from "@/types/customMetrics";

import {
  GK_CATEGORICAL_METRICS,
  METRIC_DESCRIPTION_CLAUSES,
  OUTFIELD_CATEGORICAL_METRICS,
  type CategoricalMetricDefinition,
  type GkMetricKey,
  type OutfieldMetricKey,
} from "./metricDefinitions";

/**
 * Valid GraphQL field keys for seasonal categorical scores
 */
type SeasonalMetricField =
  | keyof PlayerDetailSeasonCategoricalScoresFragment
  | "epm";

/**
 * Individual metric type for outfield skill insights.
 * Derived from {@link OUTFIELD_CATEGORICAL_METRICS} so adding a metric there
 * (SE-4168) automatically extends the selectable insight metrics.
 */
export type IndividualMetricType = OutfieldMetricKey;

/**
 * Individual metric type for goalkeeper skill insights.
 * Derived from {@link GK_CATEGORICAL_METRICS}.
 */
export type GkIndividualMetricType = GkMetricKey;

/**
 * Configuration for an individual metric
 */
export interface IndividualMetricConfig {
  /** Unique identifier for the metric */
  readonly type: IndividualMetricType;
  /** Display label for the metric */
  readonly label: string;
  /** GraphQL field name for seasonal categorical scores */
  readonly graphqlField: SeasonalMetricField;
}

/**
 * Mock AI Scouting Summary data
 */
export const MOCK_AI_SCOUTING_SUMMARY: AIScoutingSummaryData = {
  content:
    "Gabriel Jesus has rediscovered his best form following an extended absence due to an ACL injury. Since returning, he has demonstrated the pressing intensity and link-up play that made him invaluable to Arsenal's system. His recent Carabao Cup hat-trick against Crystal Palace showcased his clinical finishing when given space in the box. Scouts note his exceptional work rate without the ball—he consistently ranks among the top forwards in Europe for pressures and tackles won. His movement between the lines creates separation for Saka and Martinelli on the flanks. However, questions remain about his durability over a full season given his injury history. Arteta has described him as someone who \"changed our world\" upon arrival, and his ability to drop deep and orchestrate attacks while also threatening the goal makes him tactically versatile. At 28, he combines experience with physical prime. Key areas to monitor: consistency in Premier League finishing (currently 5 goals in 9 appearances) and managing minutes to prevent re-injury. His pressing triggers and defensive contributions make him irreplaceable in Arsenal's...",
  lastGenerated: "Jan 10, 2026 at 3:42 PM",
};

/**
 * Mock scouting reports data
 */
export const MOCK_SCOUTING_REPORTS: readonly ScoutingReport[] = [
  {
    id: "report-1",
    date: "Dec 21, 2024",
    displayText: "vs Crystal Palace",
    reportType: "Match Report",
    author: "Marcus Webb",
  },
  {
    id: "report-2",
    date: "Dec 17, 2024",
    displayText: "vs Ipswich Town",
    reportType: "Performance Analysis",
    author: "Sarah Collins",
  },
  {
    id: "report-3",
    date: "Dec 8, 2024",
    displayText: "vs Fulham",
    reportType: "Match Report",
    author: "James O'Connor",
  },
  {
    id: "report-4",
    date: "Nov 30, 2024",
    displayText: "vs West Ham",
    reportType: "Tactical Review",
    author: "Elena Rodriguez",
  },
  {
    id: "report-5",
    date: "Nov 23, 2024",
    displayText: "vs Nottingham Forest",
    reportType: "Match Report",
    author: "David Chen",
  },
  {
    id: "report-6",
    date: "Nov 9, 2024",
    displayText: "vs Chelsea",
    reportType: "Opposition Scout",
    author: "Michael Torres",
  },
];

/**
 * Mock player notes data
 */
export const MOCK_PLAYER_NOTES: readonly PlayerNote[] = [
  {
    id: "note-1",
    category: "Medical Staff",
    date: "Dec 18, 2024",
    content: "Appears to be super fit post-injury",
    isPrivate: false,
    userId: "mock-user-1",
    authorName: "John Smith",
    shareCount: 0,
    isSharedWithMe: false,
  },
  {
    id: "note-2",
    category: "Agent Contact",
    date: "Dec 10, 2024",
    content: "Player's wife wants to move back to Brazil",
    isPrivate: true,
    userId: "mock-user-2",
    authorName: "Jane Doe",
    shareCount: 0,
    isSharedWithMe: false,
  },
];

/**
 * Available compare categories for Performance Analytics
 */
export const COMPARE_CATEGORIES: readonly CompareCategoryOption[] = [
  { id: "leagues", label: "Leagues" },
  { id: "position", label: "Position" },
  { id: "watch_list", label: "Watch List" },
  { id: "players", label: "Individual Players" },
];

/**
 * Compare category options for Performance Analytics dropdown
 */
export const COMPARE_CATEGORY_OPTIONS: readonly CompareItem[] = [
  { id: "players", name: "Player" },
  { id: "watch_list", name: "Watchlist" },
  { id: "position", name: "Position" },
  { id: "leagues", name: "League" },
];

/**
 * Page size for the "Compare vs. Player" submenu search (SE-7111).
 *
 * The search is a cursor-paginated connection, so this is the size of ONE
 * page, not a cap: the submenu pages through the rest on demand. It was a
 * hardcoded `10` with no paging at all, which made any player the backend
 * ranked below the first ten unreachable however exactly their name was typed.
 * 20 matches `DEFAULT_PAGE_SIZE` in `@/hooks/usePlayerSearchPagination`, the
 * size every other player-search surface already requests.
 */
export const COMPARE_PLAYER_SEARCH_PAGE_SIZE = 20;

/**
 * Submenu title map by category ID
 */
export const SUBMENU_TITLE_MAP: Record<string, string> = {
  players: "Select Player",
  watch_list: "Select Watchlist",
  position: "Select Position",
  leagues: "Select League",
};

/**
 * Submenu search placeholder map by category ID
 */
export const SUBMENU_PLACEHOLDER_MAP: Record<string, string> = {
  players: "Search players...",
  watch_list: "Search watchlists...",
  position: "Search positions...",
  leagues: "Search leagues...",
};

/**
 * Outfield metric labels for Performance Analytics.
 * Categorical attributes (SE-4168) come from {@link OUTFIELD_CATEGORICAL_METRICS};
 * Physical Score and GPR are global-only and appended explicitly.
 */
export const OUTFIELD_METRIC_LABELS: readonly string[] = [
  ...OUTFIELD_CATEGORICAL_METRICS.map(m => m.label),
  "Physical Score",
  "GPR",
];

/**
 * Default outfield labels for the radar chart (initial selection)
 */
export const OUTFIELD_DEFAULT_LABELS = [
  "Carrying",
  "Dribbling",
  "Passing",
  "Shooting",
  "Physical Score",
  "GPR",
] as const;

/**
 * Goalkeeper metric labels for Performance Analytics.
 * Categorical attributes (SE-4168) come from {@link GK_CATEGORICAL_METRICS};
 * Physical Score and GPR are global-only and appended explicitly.
 */
export const GK_METRIC_LABELS: readonly string[] = [
  ...GK_CATEGORICAL_METRICS.map(m => m.label),
  "Physical Score",
  "GPR",
];

/**
 * Default goalkeeper labels for the radar chart (initial selection)
 */
export const GK_DEFAULT_LABELS = [
  "GK Passing",
  "Shot Stopping",
  "Positioning",
  "Physical Score",
  "GPR",
] as const;

/**
 * Returns the appropriate metric labels based on player position
 * @param isGoalkeeper - Whether the player is a goalkeeper
 * @returns Metric labels array for the player's position type
 */
export const getMetricLabelsForPosition = (
  isGoalkeeper: boolean
): readonly string[] =>
  isGoalkeeper ? GK_METRIC_LABELS : OUTFIELD_METRIC_LABELS;

/**
 * Returns metric labels with dynamically appended custom metrics.
 * Deduplicates custom labels that case-insensitively match base labels
 * (e.g., custom "Passing" is excluded since standard "Passing" already exists).
 * @param isGoalkeeper - Whether the player is a goalkeeper
 * @param customStats - Optional backend custom stats JSONB object
 * @returns Base metric labels plus deduplicated custom metric display names
 */
export const getMetricLabelsWithCustom = (
  isGoalkeeper: boolean,
  customStats?: BackendCustomStats | null
): readonly string[] => {
  const baseLabels = getMetricLabelsForPosition(isGoalkeeper);
  if (!customStats) return baseLabels;

  const baseLabelsLower = new Set(baseLabels.map(label => label.toLowerCase()));
  const customLabels = processDynamicCustomMetrics(customStats)
    .map(m => m.displayName)
    .filter(label => !baseLabelsLower.has(label.toLowerCase()));

  return [...baseLabels, ...customLabels];
};

/**
 * Returns the appropriate default labels based on player position
 * @param isGoalkeeper - Whether the player is a goalkeeper
 * @returns Default labels array for the player's position type
 */
export const getDefaultLabelsForPosition = (
  isGoalkeeper: boolean
): readonly string[] =>
  isGoalkeeper ? GK_DEFAULT_LABELS : OUTFIELD_DEFAULT_LABELS;

/** Padding around the radar chart for selector labels (one side) */
export const SELECTOR_PADDING = 80;

/** Spider chart size constant */
export const SPIDER_CHART_SIZE = 340;

/** Dot plot size constant */
export const DOT_PLOT_SIZE = 220;

/** Fixed container height to prevent layout shift when switching views */
export const CHART_CONTAINER_HEIGHT = 520;

/**
 * Metric configurations for individual metric insights.
 * Derived from {@link OUTFIELD_CATEGORICAL_METRICS} (SE-4168): the per-season
 * field uses each metric's `individualField` when set (e.g. Shooting keeps its
 * global field) and otherwise its positional field.
 */
export const INDIVIDUAL_METRIC_CONFIGS: Record<
  IndividualMetricType,
  IndividualMetricConfig
> = OUTFIELD_CATEGORICAL_METRICS.reduce(
  (acc, metric) => ({
    ...acc,
    [metric.key]: {
      type: metric.key,
      label: metric.label,
      graphqlField: ((metric as CategoricalMetricDefinition).individualField ??
        metric.positionalField) as SeasonalMetricField,
    },
  }),
  {} as Record<IndividualMetricType, IndividualMetricConfig>
);

/**
 * Ordered list of outfield metrics for dropdown
 */
export const INDIVIDUAL_METRIC_OPTIONS: readonly IndividualMetricType[] =
  OUTFIELD_CATEGORICAL_METRICS.map(m => m.key);

/**
 * Default metric to display on initial load
 */
export const DEFAULT_INDIVIDUAL_METRIC: IndividualMetricType = "passing";

/**
 * Goalkeeper metric configurations for individual metric insights.
 * Derived from {@link GK_CATEGORICAL_METRICS} (SE-4168).
 */
export const GK_INDIVIDUAL_METRIC_CONFIGS: Record<
  GkIndividualMetricType,
  IndividualMetricConfig
> = GK_CATEGORICAL_METRICS.reduce(
  (acc, metric) => ({
    ...acc,
    [metric.key]: {
      type: metric.key as unknown as IndividualMetricType,
      label: metric.label,
      graphqlField: metric.positionalField as SeasonalMetricField,
    },
  }),
  {} as Record<GkIndividualMetricType, IndividualMetricConfig>
);

/**
 * Ordered list of GK metrics for dropdown
 */
export const GK_INDIVIDUAL_METRIC_OPTIONS: readonly GkIndividualMetricType[] =
  GK_CATEGORICAL_METRICS.map(m => m.key);

/**
 * Default GK metric to display on initial load
 */
export const DEFAULT_GK_INDIVIDUAL_METRIC: GkIndividualMetricType = "gkPassing";

/**
 * Metric description for Performance Analytics info modal
 */
export interface MetricDescription {
  /** Display label for the metric */
  readonly label: string;
  /** Description explaining what the metric measures */
  readonly description: string;
}

/**
 * Descriptions for Performance Analytics metrics shown in the info modal
 */
/**
 * Composes a standard "league-adjusted blend" metric description from its
 * metric-specific clause, keeping the shared lead-in sentence in one place.
 * @param detail - The metric-specific clause that completes the sentence
 * @returns The full metric description sentence
 */
const leagueAdjustedBlend = (detail: string): string =>
  `This metric is a league-adjusted blend of their performance over the last 3 years for ${detail}`;

/**
 * Builds metric descriptions (SE-4456) for the categorical metrics that have a
 * description clause in {@link METRIC_DESCRIPTION_CLAUSES}, generating them from
 * data instead of hand-writing one object literal per attribute.
 * @param metrics - Categorical metric definitions to build descriptions for
 * @returns Metric descriptions for the metrics that have a description clause
 */
const buildClauseDescriptions = (
  metrics: readonly CategoricalMetricDefinition[]
): readonly MetricDescription[] =>
  metrics
    .filter(metric => METRIC_DESCRIPTION_CLAUSES[metric.key])
    .map(metric => ({
      label: metric.label,
      description: leagueAdjustedBlend(METRIC_DESCRIPTION_CLAUSES[metric.key]),
    }));

/**
 * Outfield metric descriptions for the info modal
 */
export const OUTFIELD_METRIC_DESCRIPTIONS: readonly MetricDescription[] = [
  {
    label: "Gemini Player Rating (GPR)",
    description:
      "This metric is a position specific, league-adjusted blend of their team's success when they are on the pitch vs off the pitch, combined with VAEP (Valuing Actions by Estimating Probabilities) - which is a framework for valuing actions that lead to goal scoring opportunities.",
  },
  {
    label: "Ball Carrying Ability",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player carries the ball and how effective those carries are at leading to goal-scoring chances.",
  },
  {
    label: "Passing",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player passes and how effective those passes are at maintaining possession and creating goal-scoring chances.",
  },
  {
    label: "Shooting",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for shot quality, conversion rate, and ability to get into scoring positions.",
  },
  {
    label: "Dribbling",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how often this player attempts to beat opponents 1v1 and their success rate in those situations.",
  },
  {
    label: "Physical Score",
    description:
      "This metric combines various physical attributes including speed, stamina, strength, and aerial ability, adjusted for positional expectations.",
  },
  // SE-4456: additional Data Hub attribute descriptions (generated from clauses)
  ...buildClauseDescriptions(OUTFIELD_CATEGORICAL_METRICS),
];

/**
 * Goalkeeper metric descriptions for the info modal
 */
export const GK_METRIC_DESCRIPTIONS: readonly MetricDescription[] = [
  {
    label: "Gemini Player Rating (GPR)",
    description:
      "This metric is a position specific, league-adjusted blend of their team's success when they are on the pitch vs off the pitch, combined with VAEP (Valuing Actions by Estimating Probabilities) - which is a framework for valuing actions that lead to goal scoring opportunities.",
  },
  {
    label: "GK Passing",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how effectively this goalkeeper distributes the ball, including goal kicks, short passes, and long-range distribution.",
  },
  {
    label: "Shot Stopping",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how effectively this goalkeeper stops shots, including save percentage, expected goals prevented, and performance on high-quality chances.",
  },
  {
    label: "Positioning",
    description:
      "This metric is a league-adjusted blend of their performance over the last 3 years for how well this goalkeeper's positioning reduces the quality of scoring chances faced.",
  },
  {
    label: "Physical Score",
    description:
      "This metric combines various physical attributes including speed, stamina, strength, and aerial ability, adjusted for positional expectations.",
  },
  // SE-4456: additional goalkeeper Data Hub attribute descriptions (generated)
  ...buildClauseDescriptions(GK_CATEGORICAL_METRICS),
];

/**
 * Returns metric descriptions based on player position
 * @param isGoalkeeper - Whether the player is a goalkeeper
 * @returns Metric descriptions for the player's position type
 */
export const getMetricDescriptionsForPosition = (
  isGoalkeeper: boolean
): readonly MetricDescription[] =>
  isGoalkeeper ? GK_METRIC_DESCRIPTIONS : OUTFIELD_METRIC_DESCRIPTIONS;

/**
 * Maps competition keys to human-readable display names.
 * Keys come from the data provider (Transfermarkt/Opta).
 */
export const COMPETITION_DISPLAY_NAMES: Record<string, string> = {
  // England
  GB1: "Premier League",
  GB2: "Championship",
  GB3: "League One",
  GB4: "League Two",
  GBFA: "FA Cup",
  GBLC: "EFL Cup",
  GBCS: "Community Shield",
  // Spain
  ES1: "La Liga",
  ES2: "La Liga 2",
  ESCR: "Copa del Rey",
  ESSC: "Supercopa",
  // Germany
  L1: "Bundesliga",
  L2: "2. Bundesliga",
  DFB: "DFB-Pokal",
  DESL: "DFL-Supercup",
  // Italy
  IT1: "Serie A",
  IT2: "Serie B",
  ITCP: "Coppa Italia",
  ITSC: "Supercoppa",
  // France
  FR1: "Ligue 1",
  FR2: "Ligue 2",
  FRCP: "Coupe de France",
  FRLC: "Coupe de la Ligue",
  // Netherlands
  NL1: "Eredivisie",
  // Portugal
  PO1: "Primeira Liga",
  // European
  "UEFA.CL": "Champions League",
  "UEFA.EL": "Europa League",
  "UEFA.UECL": "Conference League",
  "UEFA.SC": "UEFA Super Cup",
  // International
  "FIFA.WC": "World Cup",
  "FIFA.WCQ": "World Cup Qualifiers",
  "FIFA.CWC": "Club World Cup",
  "UEFA.EURO": "Euros",
  "UEFA.ECQ": "Euro Qualifiers",
  "UEFA.NL": "Nations League",
  // Friendlies
  FS: "Friendly",
  INT_F: "International Friendly",
};

/**
 * Get display name for a competition key
 * @param key - Competition key from data provider (e.g., "GB1", "UEFA.CL")
 * @returns Human-readable competition name, or the raw key if not mapped
 */
export function getCompetitionDisplayName(
  key: string | null | undefined
): string {
  if (!key) return "Unknown";
  return COMPETITION_DISPLAY_NAMES[key] ?? key;
}
